import type { RefObject } from "react";
import { SheetFooter } from "@/components/ui/sheet";
import { formatPrice } from "@/utils/ShopifyHelpers";
import { DiscountedPrice } from "@/components/common";
import Button from "@/components/common/Button";
import { BTN_TYPES } from "@/utils/constants";
import type { GiftForm } from "./hooks";
import FreeShippingBanner from "./FreeShippingBanner";

type SideCartFooterProps = {
  showGiftForm: boolean;
  hasSingleMatic: boolean;
  totalQuantity: number;
  subtotalValue: number;
  originalSubtotalValue: number;
  currencyCode: string;
  giftForm: GiftForm;
  isGift: boolean;
  handleGoToCheckout: () => Promise<void>;
  setShowGiftForm: (value: boolean) => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  hasFreeShipping: boolean;
  shippingRemaining: number;
  shippingProgress: number;
  disableCheckout?: boolean;
};

export default function SideCartFooter({
  showGiftForm,
  hasSingleMatic,
  totalQuantity,
  subtotalValue,
  originalSubtotalValue,
  currencyCode,
  giftForm,
  isGift,
  handleGoToCheckout,
  setShowGiftForm,
  scrollContainerRef,
  hasFreeShipping,
  shippingRemaining,
  shippingProgress,
  disableCheckout = false,
}: SideCartFooterProps) {
  const displaySubtotal = formatPrice(subtotalValue, currencyCode);
  const shouldShowOriginalSubtotal = originalSubtotalValue > subtotalValue;
  const displayOriginalSubtotal = formatPrice(
    originalSubtotalValue,
    currencyCode,
  );
  const checkoutPriceLabel = shouldShowOriginalSubtotal ? (
    <>
      <span>CHECKOUT | </span>
      <DiscountedPrice
        currentPrice={displaySubtotal}
        originalPrice={displayOriginalSubtotal}
        className="inline-flex items-center"
        currentPriceClassName="order-last ml-[8px]"
        originalPriceClassName="order-first line-through opacity-70 [text-decoration-skip-ink:none]"
      />
    </>
  ) : (
    <>{`CHECKOUT | ${displaySubtotal}`}</>
  );

  return (
    <SheetFooter className="shrink-0 !flex-col !justify-start !space-x-0 bg-[#faf9f7] pb-9">
      <div className="flex w-full flex-col gap-4 px-8 pt-4 md:px-[48px]">
        {!(showGiftForm && hasSingleMatic) && (
          <div className="flex w-full flex-col">
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-ppMori text-[16px] font-bold leading-[1.6] tracking-[-0.32px] text-black">
                  Total
                </span>
                <span className="font-trimMono text-[14px] uppercase tracking-[0.42px] text-[#54524e]">
                  [{totalQuantity} {totalQuantity === 1 ? "ITEM" : "ITEMS"}]
                </span>
              </div>
              <div className="flex items-center gap-[8px] font-trimMono text-[16px] font-semibold">
                {shouldShowOriginalSubtotal ? (
                  <DiscountedPrice
                    currentPrice={displaySubtotal}
                    originalPrice={displayOriginalSubtotal}
                    className="flex items-center gap-[8px]"
                    currentPriceClassName="order-last text-[#1f2022]"
                    originalPriceClassName="order-first text-[#54524e] line-through [text-decoration-skip-ink:none]"
                  />
                ) : (
                  <span className="text-[#1f2022]">{displaySubtotal}</span>
                )}
              </div>
            </div>
            <FreeShippingBanner
              hasFreeShipping={hasFreeShipping}
              shippingRemaining={shippingRemaining}
              currencyCode={currencyCode}
              shippingProgress={shippingProgress}
            />
          </div>
        )}
        <div className="w-full">
          {showGiftForm && hasSingleMatic ? (
            <Button
              onClickFn={async () => {
                await giftForm.commitAttributes();
                await handleGoToCheckout();
              }}
              btnType={BTN_TYPES.PRIMARY}
              disabled={!giftForm.isValid || disableCheckout}
              customClasses="w-full !h-[56px] flex items-center justify-center disabled:!opacity-50 disabled:!bg-legacy-primary disabled:!text-legacy-white"
            >
              <span className="font-trimMono text-[16px] uppercase leading-[1.6] tracking-[0.48px]">
                {checkoutPriceLabel}
              </span>
            </Button>
          ) : (
            <Button
              onClickFn={
                isGift && hasSingleMatic
                  ? () => {
                      setShowGiftForm(true);
                      scrollContainerRef.current?.scrollTo(0, 0);
                    }
                  : handleGoToCheckout
              }
              btnType={BTN_TYPES.PRIMARY}
              disabled={disableCheckout}
              customClasses="w-full !h-[56px] flex items-center justify-center disabled:!opacity-50 disabled:!bg-legacy-primary disabled:!text-legacy-white"
            >
              <span className="font-trimMono text-[16px] uppercase leading-[1.6] tracking-[0.48px]">
                {isGift && hasSingleMatic
                  ? "CONTINUE TO GIFT DETAILS"
                  : checkoutPriceLabel}
              </span>
            </Button>
          )}
        </div>
      </div>
    </SheetFooter>
  );
}
